import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as CloseIcon } from "../assets/close_icon.svg";
import { ReactComponent as Logo } from "../assets/uido_logo_black.svg";
import Navigation from "./Navigation.jsx";
import { ReactComponent as Message } from "../assets/message.svg";
import { switchLanguageWithFade } from "../utils/languageSwitch";
// import { useTheme } from "../context/ThemeContext"; // Unused for now
import ButtonWithIcon from "./ButtonWithIcon";

const MobileMenuComponent = ({ buttonOnClickPrimary, toggleMenu }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isConsultationOpen, setConsultationOpen] = useState(false);
  const [isConsultationClosing, setConsultationClosing] = useState(false);
  const [isLanguageSwitching, setIsLanguageSwitching] = useState(false);
  const overlayRef = useRef(null);
  const closeButtonRef = useRef(null);
  // const { isMatrixEnabled, toggleMatrix } = useTheme(); // Unused for now

  const changeLanguage = async (lng) => {
    if (isLanguageSwitching) return;

    setIsLanguageSwitching(true);
    try {
      await switchLanguageWithFade({
        lng,
        i18n,
        location,
        navigate,
      });
    } finally {
      setIsLanguageSwitching(false);
    }
  };

  const closeConsultation = () => setConsultationOpen(false);

  useEffect(() => {
    const onClickOutside = (e) => {
      const wrapper = document.querySelector('.mobile-consultation-wrapper');
      if (wrapper && !wrapper.contains(e.target)) {
        if (isConsultationOpen) {
          setConsultationOpen(false);
          setConsultationClosing(true);
          setTimeout(() => setConsultationClosing(false), 450);
        }
      }
    };
    if (isConsultationOpen) {
      document.addEventListener('click', onClickOutside);
    }
    return () => document.removeEventListener('click', onClickOutside);
  }, [isConsultationOpen]);

  useEffect(() => {
    const previouslyFocusedElement = document.activeElement;
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        toggleMenu();
        return;
      }

      if (event.key !== "Tab" || !overlayRef.current) return;

      const focusableElements = Array.from(
        overlayRef.current.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocusedElement?.focus?.();
    };
  }, [toggleMenu]);

  const handleOptionConsultation = () => {
    closeConsultation();
    buttonOnClickPrimary();
  };

  const handleOptionForm = () => {
    closeConsultation();
    const form = document.querySelector('#form');
    if (form) {
      toggleMenu();
      const top = form.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: top + 80, behavior: 'smooth' });
      return;
    }
    toggleMenu();
    navigate(`${location.pathname.startsWith("/en") ? "/en" : ""}/#form`);
  };

  const handleOptionPhone = () => {
    closeConsultation();
    toggleMenu(); // Close mobile menu
    const footer = document.querySelector('[data-section="contact-footer"]');
    if (footer) {
      footer.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.open(
        'https://wa.me/48516720692?text=Cze%C5%9B%C4%87!%20Chcia%C5%82_bym%20um%C3%B3wi%C4%87%20si%C4%99%20na%20konsultacj%C4%99%20z%20UIDO.',
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  const handleOptionEmail = () => {
    closeConsultation();
    toggleMenu(); // Close mobile menu
    const footer = document.querySelector('[data-section="contact-footer"]');
    if (footer) {
      footer.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.open(
        'mailto:kontakt@uido.pl?subject=Konsultacja%20z%20UIDO&body=Cze%C5%9B%C4%87!%20Chcia%C5%82_bym%20um%C3%B3wi%C4%87%20si%C4%99%20na%20konsultacj%C4%99%20z%20UIDO.'
      );
    }
  };

  return (
    <div
      className="mobile-menu-overlay"
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="mobile-menu-title"
    >
      <div className="mobile-menu-header">
        <div className="logo">
          <Logo />
          <div className="lang">
            <button
              className={`clickable-button polish ${
                i18n.language === "pl" ? "active" : ""
              }`}
              onClick={() => changeLanguage("pl")}
              disabled={isLanguageSwitching}
            >
              PL
            </button>
            <button
              className={`clickable-button english ${
                i18n.language === "en" ? "active" : ""
              }`}
              onClick={() => changeLanguage("en")}
              disabled={isLanguageSwitching}
            >
              EN
            </button>
          </div>
        </div>

        <button
          type="button"
          className="close-icon"
          onClick={toggleMenu}
          ref={closeButtonRef}
          aria-label={t("menu.close")}
        >
          <CloseIcon />
        </button>
      </div>

      <div className="mobile-menu-content">
        <h3 className="menu-title" id="mobile-menu-title">{t("menu.title")}</h3>
        {/* removed toggle in mobile menu per request */}
        <Navigation onLinkClick={toggleMenu} />
        
        <div className={`mobile-consultation-wrapper consultation-wrapper${isConsultationOpen ? ' is-open' : ''}${isConsultationClosing ? ' is-closing' : ''}`}>
          <ButtonWithIcon onClick={(e) => { e.stopPropagation(); setConsultationOpen(!isConsultationOpen); }} iconLeft={Message}>
            {t("cta.consultation")}
          </ButtonWithIcon>
          <div className="consultation-dropdown" role="menu" aria-label="consultation-options">
            <button type="button" className="consultation-option" onClick={handleOptionConsultation}>
              <span className="option-label">{t("consultation.option.15min")}</span>
            </button>
            <button type="button" className="consultation-option" onClick={handleOptionForm}>
              <span className="option-label">{t("consultation.option.form")}</span>
            </button>
            <button type="button" className="consultation-option" onClick={handleOptionPhone}>
              <span className="option-label">{t("consultation.option.phone")}</span>
            </button>
            <button type="button" className="consultation-option" onClick={handleOptionEmail}>
              <span className="option-label">{t("consultation.option.email")}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileMenuComponent;
